import type { MaterialSrc } from "common/materials/materialSrc";
import type { AABB2 } from "geom/aabb2Types";
import type { Plane } from "geom/plane";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { UVGeneration } from "geom/uv";
import type { HatchMaterialId } from "./hatchMaterialId";

export type ProceduralHatchFunction = (aabb2: AABB2) => PolyLine2[];

export enum HatchMaterialType {
  PROCEDURAL = "PROCEDURAL",
  PATTERN = "PATTERN",
  DESIGN = "DESIGN",
}

export interface HatchProceduralMaterial {
  type: HatchMaterialType.PROCEDURAL;
  materialId: HatchMaterialId;
}

export interface HatchPatternMaterial {
  type: HatchMaterialType.PATTERN;
  patternLines: HatchPatternLine[];
}

export interface HatchDesignMaterial {
  type: HatchMaterialType.DESIGN;
  sizeU: number;
  sizeV: number;
  rotation: number;
  lines: PolyLine2[];
}

export type HatchMaterial = HatchProceduralMaterial | HatchPatternMaterial | HatchDesignMaterial;

export interface HatchPatternLine {
  angle: number;
  originX: number;
  originY: number;
  deltaX: number;
  deltaY: number;
  incX: number;
  dash: number[];
}

export interface HatchProperties {
  materialId?: MaterialSrc;
  materialKey?: string;
  hatchMaterial?: HatchMaterial;
  uvGeneration?: UVGeneration;
  plane: Plane;
}

export const HATCH_POINT_SIZE = 0.01;

export function hatchPatternLineNew(
  angle: number,
  originX: number,
  originY: number,
  deltaX: number,
  deltaY: number,
  dash: number[] = [],
): HatchPatternLine {
  let incX = dash.length > 0 ? 0 : 1;
  for (let i = 0, l = dash.length; i < l; i++) {
    incX += Math.abs(dash[i]);
  }

  incX = Math.max(incX, HATCH_POINT_SIZE);

  return {
    angle,
    originX,
    originY,
    deltaX,
    deltaY,
    incX,
    dash,
  };
}

export function hatchPropertiesNew(
  plane: Plane,
  uvGeneration?: UVGeneration,
  materialId?: MaterialSrc,
  materialKey?: string,
  hatchMaterial?: HatchMaterial,
): HatchProperties {
  return {
    materialId,
    materialKey,
    hatchMaterial,
    plane,
    uvGeneration,
  };
}

export function hatchProceduralMaterialNew(materialId: HatchMaterialId): HatchProceduralMaterial {
  return {
    type: HatchMaterialType.PROCEDURAL,
    materialId,
  };
}

export function hatchPatternMaterialNew(patternLines: HatchPatternLine[]): HatchPatternMaterial {
  return {
    type: HatchMaterialType.PATTERN,
    patternLines,
  };
}

export function hatchDesignMaterialNew(
  sizeU: number,
  sizeV: number,
  rotation: number = 0,
  lines: PolyLine2[] = [],
): HatchDesignMaterial {
  return {
    type: HatchMaterialType.DESIGN,
    sizeU,
    sizeV,
    rotation,
    lines,
  };
}
